// AccessLogPage.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

export default function AccessLogPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [usersCache, setUsersCache] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch and cache a user profile (for avatar/display name)
  const ensureProfile = useCallback(async (username) => {
    if (!username || usersCache[username]) return;
    try {
      const res = await fetch(`/users/${encodeURIComponent(username)}`);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const profile = await res.json();
      setUsersCache((prev) => ({ ...prev, [username]: profile || {} }));
    } catch {
      setUsersCache((prev) => ({ ...prev, [username]: {} }));
    }
  }, [usersCache]);

  const avatarFor = (username) =>
    (usersCache[username]?.avatar_url) || '/default-avatar.png';

  const displayName = (username) =>
    (usersCache[username]?.display_name) || username || '—';

  // 1) Load access logs
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetch('/access-log', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Erreur HTTP ' + res.status);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setLogs(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Erreur chargement logs :', err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [token]);

  // 2) Hydrate requester & validator profiles
  useEffect(() => {
    if (!logs?.length) return;
    const needed = new Set();
    for (const log of logs) {
      if (log.user_id && !usersCache[log.user_id]) needed.add(log.user_id);
      if (log.decided_by && !usersCache[log.decided_by]) needed.add(log.decided_by);
    }
    if (needed.size === 0) return;

    (async () => {
      await Promise.all(Array.from(needed).map((u) => ensureProfile(u)));
    })();
  }, [logs, usersCache, ensureProfile]);

  const sortedLogs = useMemo(
    () => [...logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [logs]
  );

  return (
    <div className="container mt-5" style={{ paddingTop: '70px' }}>
      <style>{`
        .flow-card { border: 0; border-radius: 14px; }
        .flow-badge { font-weight: 600; }
        .flow-sep { height: 2px; background: rgba(0,0,0,0.06); border-radius: 2px; margin: .25rem 0 .5rem 0; }
        .flow-pill {
          display: inline-flex; align-items: center; gap: .35rem;
          padding: .25rem .6rem; border-radius: 999px; background: #f8f9fa; border: 1px solid #e9ecef;
          font-size: .85rem;
        }
        .avatar-48 { width: 48px; height: 48px; object-fit: cover; border-radius: 50%; }
        .flow-arrow { font-size: 1.1rem; color: #6c757d; }
        .flow-meta { color: #6c757d; font-size: .9rem; }
        .left-accent { border-left-width: 6px !important; }
      `}</style>

      <h2 className="mb-4">Workflow d’accès — demandes et validations</h2>

      {loading ? (
        <p className="text-muted">Chargement…</p>
      ) : sortedLogs.length === 0 ? (
        <p className="text-muted">Aucune demande d’accès pour le moment.</p>
      ) : (
        <div className="row">
          {sortedLogs.map((log) => {
            const date = new Date(log.created_at).toLocaleString();
            const decision = (log.decision || '').toLowerCase();
            const approved = decision === 'approved';
            const denied = decision === 'denied';
            const pending = !approved && !denied;

            const accent = approved ? '#198754' : denied ? '#dc3545' : '#ffc107';

            return (
              <div key={log.id} className="col-12 col-lg-6 mb-3">
                <div className="card flow-card shadow-sm left-accent" style={{ borderLeft: `6px solid ${accent}` }}>
                  <div className="card-body">
                    {/* Header: project + status */}
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="fw-bold">
                        <i className="bi bi-folder2-open me-2"></i>
                        Projet #{log.project_id ?? '—'}
                      </div>
                      <div>
                        {approved && (
                          <span className="badge bg-success flow-badge">
                            <i className="bi bi-check-circle me-1"></i> Approuvé
                          </span>
                        )}
                        {denied && (
                          <span className="badge bg-danger flow-badge">
                            <i className="bi bi-x-circle me-1"></i> Refusé
                          </span>
                        )}
                        {pending && (
                          <span className="badge bg-warning text-dark flow-badge">
                            <i className="bi bi-hourglass-split me-1"></i> En attente
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flow-sep" />

                    {/* FLOW: requester -> validator */}
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      {/* Requester */}
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={avatarFor(log.user_id)}
                          alt={displayName(log.user_id)}
                          className="avatar-48"
                        />
                        <div>
                          <div className="fw-semibold">{displayName(log.user_id)}</div>
                          <div className="flow-meta">
                            <i className="bi bi-person-plus me-1"></i>
                            A demandé l’accès
                          </div>
                        </div>
                      </div>

                      {/* Arrow */}
                      <i className="bi bi-arrow-right flow-arrow"></i>

                      {/* Validator (or pending) */}
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={pending ? '/default-avatar.png' : avatarFor(log.decided_by)}
                          alt={pending ? 'En attente' : displayName(log.decided_by)}
                          className="avatar-48"
                        />
                        <div>
                          <div className="fw-semibold">
                            {pending ? '—' : displayName(log.decided_by)}
                          </div>
                          <div className="flow-meta">
                            <i className="bi bi-shield-check me-1"></i>
                            {pending ? 'Validation en attente' : 'A validé la demande'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Decision meta / reason */}
                    {(log.reason || !pending) && (
                      <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
                        {!pending && (
                          <span className="flow-pill">
                            <i className="bi bi-clipboard-check"></i>
                            Décision : {approved ? 'Approuvé' : 'Refusé'}
                          </span>
                        )}
                        {log.reason && (
                          <span className="flow-pill">
                            <i className="bi bi-chat-left-text"></i>
                            Motif : {log.reason}
                          </span>
                        )}
                        <span className="flow-pill">
                          <i className="bi bi-clock-history"></i>
                          {date}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
